/* Predictive search - Be Yours, rivisto da Digital Runner
   Cambia rispetto all'originale:
   - chiede a Shopify fino a 10 risultati per tipo (prodotti, categorie, suggerimenti,
     pagine, articoli) invece di 3 in tutto
   - frecce su/giu' passano da tutte le opzioni (prodotti, chip, link, "vedi tutti")
   - "Ricerche recenti" salvate nel browser e mostrate prima di scrivere
   - corretto l'errore quando si svuota il campo (clear senza evento)
   Il contenuto del menu arriva da sections/predictive-search.liquid. */
'use strict';

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';
    this.abort = null;
    this.setupEventListeners();
    this.renderRecent();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (form) form.addEventListener('submit', this.onFormSubmit.bind(this));
    const closeBtn = this.querySelector('button[type="button"].search__button');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    const resetBtn = this.querySelector('button[type="reset"]');
    if (resetBtn) resetBtn.addEventListener('click', this.clear.bind(this));
    const run = typeof debounce === 'function' ? debounce(this.onChange.bind(this), 250) : this.onChange.bind(this);
    this.input.addEventListener('input', run);
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    // click su un risultato: salvo la ricerca tra le recenti
    this.predictiveSearchResults.addEventListener('click', (e) => {
      if (e.target.closest('a')) this.saveRecent(this.getQuery());
    });
    const clr = this.querySelector('[data-ps-recent-clear]');
    if (clr) clr.addEventListener('click', () => { this.writeRecent([]); this.renderRecent(); this.input.focus(); });
  }

  getQuery() { return this.input.value.trim(); }

  onChange() {
    const q = this.getQuery();
    this.searchTerm = q;
    if (q.length) this.getSearchResults(q);
    else { this.clear(); this.close(true); }
  }

  onFormSubmit(e) {
    const selected = this.querySelector('[aria-selected="true"] a');
    if (!this.getQuery().length || selected) { e.preventDefault(); if (selected) selected.click(); return; }
    this.saveRecent(this.getQuery());
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');
    const q = this.getQuery();
    if (!q.length) return;
    if (this.searchTerm !== q) this.onChange();
    else if (this.getAttribute('results') === 'true') this.open();
    else this.getSearchResults(this.searchTerm);
  }

  onFocusOut() {
    setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); });
  }

  onKeyup(e) {
    if (!this.getQuery().length) { this.clear(); this.close(true); }
    switch (e.code) {
      case 'ArrowUp': this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      default: break;
    }
  }

  onKeydown(e) {
    if (e.code === 'ArrowUp' || e.code === 'ArrowDown') e.preventDefault();
  }

  options() {
    return Array.prototype.slice.call(this.predictiveSearchResults.querySelectorAll('[role="option"]'))
      .filter((el) => el.offsetParent !== null);
  }

  switchOption(dir) {
    if (!this.getAttribute('open')) return;
    const list = this.options();
    if (!list.length) return;
    const current = this.querySelector('[aria-selected="true"]');
    let i = list.indexOf(current);
    if (dir === 'up') i = i <= 0 ? list.length - 1 : i - 1;
    else i = i >= list.length - 1 ? 0 : i + 1;
    const next = list[i];
    if (current) current.setAttribute('aria-selected', 'false');
    next.setAttribute('aria-selected', 'true');
    next.scrollIntoView({ block: 'nearest' });
    this.input.setAttribute('aria-activedescendant', next.id);
    this.setLiveRegionText(next.textContent.replace(/\s+/g, ' ').trim());
  }

  getSearchResults(term) {
    const key = term.toLowerCase();
    this.setLiveRegionLoadingState();
    if (this.cachedResults[key]) {
      this.renderSearchResults(this.cachedResults[key]);
      return;
    }
    if (this.abort) this.abort.abort();
    this.abort = typeof AbortController === 'function' ? new AbortController() : null;
    const params = new URLSearchParams();
    params.set('q', term);
    params.set('resources[type]', 'product,collection,query,page,article');
    params.set('resources[limit]', '10');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('section_id', 'predictive-search');
    fetch(`${window.routes.predictive_search_url}?${params.toString()}`, this.abort ? { signal: this.abort.signal } : {})
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then((text) => {
        const el = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        const html = el ? el.innerHTML : '';
        this.allPredictiveSearchInstances.forEach((inst) => { inst.cachedResults[key] = html; });
        // mostro solo se nel frattempo non si e' scritto altro
        if (this.getQuery().toLowerCase() === key) this.renderSearchResults(html);
      })
      .catch((err) => { if (err && err.name === 'AbortError') return; this.removeAttribute('loading'); });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(text) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text || '';
    setTimeout(() => { this.statusElement.setAttribute('aria-hidden', 'true'); }, 1000);
  }

  renderSearchResults(html) {
    this.predictiveSearchResults.innerHTML = html;
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    const live = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (live) this.setLiveRegionText(live.textContent.trim());
    if (this.modal) this.modal.classList.add('searching');
    this.open();
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.header');
    this.resultsMaxHeight = window.innerHeight - (header ? header.getBoundingClientRect().bottom : 0);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = (this.resultsMaxHeight || this.getResultsMaxHeight()) + 'px';
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
  }

  close(clearTerm) {
    this.closeResults(clearTerm === true);
    this.isOpen = false;
  }

  closeResults(clearTerm) {
    if (clearTerm) { this.input.value = ''; this.removeAttribute('results'); }
    const sel = this.querySelector('[aria-selected="true"]');
    if (sel) sel.setAttribute('aria-selected', 'false');
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    document.body.classList.remove('predictive-search--focus');
  }

  clear(e) {
    if (e && e.preventDefault) e.preventDefault();
    this.input.value = '';
    this.searchTerm = '';
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.predictiveSearchResults.innerHTML = '';
    if (this.modal) this.modal.classList.remove('searching');
    this.renderRecent();
    this.input.focus();
  }

  /* ---- Ricerche recenti (solo in questo browser) ---- */
  readRecent() {
    try { return JSON.parse(window.localStorage.getItem('dr_ricerche_recenti') || '[]') || []; } catch (e) { return []; }
  }

  writeRecent(list) {
    try { window.localStorage.setItem('dr_ricerche_recenti', JSON.stringify(list)); } catch (e) {}
  }

  saveRecent(q) {
    q = (q || '').trim();
    if (q.length < 2) return;
    const list = this.readRecent().filter((x) => x.toLowerCase() !== q.toLowerCase());
    list.unshift(q);
    this.writeRecent(list.slice(0, 6));
  }

  renderRecent() {
    const box = this.querySelector('[data-ps-recent]');
    const ul = this.querySelector('[data-ps-recent-list]');
    if (!box || !ul) return;
    const list = this.readRecent();
    ul.innerHTML = '';
    list.forEach((q) => {
      const li = document.createElement('li');
      li.className = 'ps-chip_dr';
      const a = document.createElement('a');
      a.href = (window.routes.search_url || '/search') + '?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last';
      a.textContent = q;
      li.appendChild(a);
      ul.appendChild(li);
    });
    box.hidden = list.length === 0;
  }
}

customElements.define('predictive-search', PredictiveSearch);
